import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserCheck, UserX, Trash2, Phone, MessageCircle, RefreshCcw, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { waLink } from "@/lib/brand";

export default function AdminStudents() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/students");
      setStudents(data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (s) => {
    try {
      await api.patch(`/admin/students/${s.id}/active`, { active: !s.active });
      toast.success(s.active ? "Student deactivated" : "Student reactivated");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this student and all their progress permanently?")) return;
    try {
      await api.delete(`/admin/students/${id}`);
      toast.success("Student deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-4xl font-800 text-[#0B192C]">Students</h1>
          <p className="text-slate-600 mt-1">All registered students. Click a student to view/add progress.</p>
        </div>
        <button onClick={load} className="vk-btn-dark text-sm"><RefreshCcw size={14} /> Refresh</button>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="students-table">
            <thead className="bg-slate-50 text-slate-600 uppercase text-xs tracking-widest">
              <tr>
                <th className="text-left px-4 py-3">Student</th>
                <th className="text-left px-4 py-3">Class · Board</th>
                <th className="text-left px-4 py-3">Phone</th>
                <th className="text-left px-4 py-3">Parent</th>
                <th className="text-left px-4 py-3">Registered</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (<tr><td colSpan={7} className="text-center px-4 py-10 text-slate-500">Loading…</td></tr>)}
              {!loading && students.length === 0 && (
                <tr><td colSpan={7} className="text-center px-4 py-10 text-slate-500">
                  No students yet. Generate <Link to="/admin/codes" className="text-[#FF6B35] hover:underline">enrollment codes</Link> and share them.
                </td></tr>
              )}
              {students.map(s => (
                <tr key={s.id} data-testid={`student-row-${s.id}`}>
                  <td className="px-4 py-4">
                    <Link to={`/admin/students/${s.id}`} className="font-medium text-[#0B192C] hover:text-[#FF6B35]">{s.name}</Link>
                  </td>
                  <td className="px-4 py-4"><span className="vk-chip text-xs">Class {s.student_class} · {s.board}</span></td>
                  <td className="px-4 py-4 text-slate-700">
                    <div className="flex items-center gap-3">
                      <a href={`tel:${s.phone}`} className="hover:text-[#FF6B35]"><Phone size={14} /></a>
                      <a href={waLink(`Hi ${s.name}, this is your teacher from Vikramshila.`)} target="_blank" rel="noreferrer" className="hover:text-[#25D366]"><MessageCircle size={14} /></a>
                      <span>{s.phone}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-slate-700">{s.parent_name || "—"}</td>
                  <td className="px-4 py-4 text-slate-500 text-xs">{new Date(s.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-4">
                    {s.active ? (
                      <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded">Active</span>
                    ) : (
                      <span className="text-xs font-semibold text-slate-500 bg-slate-100 border border-slate-200 px-2 py-1 rounded">Inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button onClick={() => toggle(s)} className="text-slate-400 hover:text-[#0B192C]" title={s.active ? "Deactivate" : "Activate"} data-testid={`toggle-${s.id}`}>
                        {s.active ? <UserX size={16} /> : <UserCheck size={16} />}
                      </button>
                      <Link to={`/admin/students/${s.id}`} className="text-slate-400 hover:text-[#FF6B35]" title="Manage" data-testid={`open-${s.id}`}>
                        <ArrowRight size={16} />
                      </Link>
                      <button onClick={() => remove(s.id)} className="text-slate-400 hover:text-red-500" title="Delete" data-testid={`del-student-${s.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-lg border border-dashed border-slate-300 p-6 bg-white">
        <h3 className="font-display text-lg font-bold text-[#0B192C]">Bulk CSV progress upload</h3>
        <p className="text-sm text-slate-600 mt-1">
          Prepare a CSV with columns: <span className="font-mono text-xs bg-slate-100 px-1 rounded">student_phone, entry_type, subject, chapter, test_name, marks_obtained, max_marks, entry_date, attendance_percent, remark, grade_value</span>.
          Upload it below to add progress entries for many students at once.
        </p>
        <CsvUploader onDone={load} />
      </div>
    </>
  );
}

function CsvUploader({ onDone }) {
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/progress/bulk-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} entries · ${data.skipped} skipped`);
      if (data.errors?.length) {
        toast.warning("Some rows had issues", { description: data.errors.slice(0, 3).join(" · ") });
      }
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="mt-4">
      <label className="vk-btn-primary text-sm cursor-pointer inline-flex" data-testid="csv-upload-label">
        {uploading ? "Uploading…" : "Upload CSV"}
        <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} disabled={uploading} data-testid="csv-upload-input" />
      </label>
    </div>
  );
}
